import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Avatar, Chip,
  Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup, MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AddCard as RequestIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Undo as ReverseIcon,
  Search as SearchIcon, Close as CloseIcon,
  Refresh as RefreshIcon, AccountBalance as BankIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import { useIsMobile, MobileDataCard } from '../../components/ResponsiveTable';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

export default function PettyApprovalsPage() {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp]   = useState(10);
  // Status filter: ALL | PENDING | APPROVED | REJECTED
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/petty/reimbursements', {
        params: {
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
          from: from || undefined,
          to:   to   || undefined,
        },
      });
      setRows(r.data || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load reimbursements'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, from, to]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r => [
      r.reimbursement_no, r.source_account, r.reason,
      r.requested_by_name, r.approved_by_name, r.rejected_reason, r.status,
    ].filter(Boolean).join(' ').toLowerCase().includes(t));
  }, [rows, q]);

  // Per-status counts for the filter chip labels (read off the loaded
  // rows so we don't need a second round-trip; "ALL" view shows totals).
  const counts = useMemo(() => {
    const c = { ALL: rows.length, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    rows.forEach(r => {
      if (c[r.status] !== undefined) c[r.status]++;
    });
    return c;
  }, [rows]);

  const approve = async (r) => {
    const ok = await swal.confirm({
      title: `Approve ${r.reimbursement_no}?`,
      text: `Move GHS ${fmtMoney(r.amount)} from ${r.source_account} into Petty Cash. This writes to the cashbook and journal.`,
      tone: 'success', confirmText: 'Yes, approve & post',
    });
    if (!ok) return;
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      await API.post(`/petty/reimbursements/${r.id}/approve`);
      swal.success(`${r.reimbursement_no} approved`);
      window.dispatchEvent(new Event('pendingCountsChanged'));
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Approval failed')); }
    finally { setBusy(b => ({ ...b, [r.id]: false })); }
  };

  const reject = async (r) => {
    const reason = await swal.prompt({
      title: `Reject ${r.reimbursement_no}?`,
      text: `${r.source_account} · GHS ${fmtMoney(r.amount)}`,
      inputLabel: 'Reason',
      inputType: 'textarea',
      confirmText: 'Reject',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      await API.post(`/petty/reimbursements/${r.id}/reject`, { reason });
      swal.success(`${r.reimbursement_no} rejected`);
      window.dispatchEvent(new Event('pendingCountsChanged'));
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Reject failed')); }
    finally { setBusy(b => ({ ...b, [r.id]: false })); }
  };

  // Reverse an APPROVED reimbursement. The backend blocks this if the
  // petty balance has been spent below the reimbursement amount (i.e.
  // the money is no longer sitting untouched), so the user sees a
  // clean "Cannot reverse, spend has happened" message instead of a
  // negative balance.
  const reverse = async (r) => {
    const reason = await swal.prompt({
      title: `Reverse ${r.reimbursement_no}?`,
      text: `Pull GHS ${fmtMoney(r.amount)} back from Petty Cash into ${r.source_account}. Only works if the money has not been spent yet.`,
      inputLabel: 'Reason for reversal',
      inputType: 'textarea',
      confirmText: 'Yes, reverse',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    setBusy(b => ({ ...b, [r.id]: true }));
    try {
      await API.post(`/petty/reimbursements/${r.id}/reverse`, { reason });
      swal.success(`${r.reimbursement_no} reversed`);
      window.dispatchEvent(new Event('pendingCountsChanged'));
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Reversal failed')); }
    finally { setBusy(b => ({ ...b, [r.id]: false })); }
  };

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 4 }}>
      <ModuleHeader
        icon={<RequestIcon />}
        title="Petty Cash · Reimbursement Approvals"
        subtitle="Pending Petty Cash Reimbursement requests awaiting approval"
        actionLabel="Refresh"
        actionIcon={<RefreshIcon />}
        onAction={load}
      />

      {/* Status + date filters */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive size="small"
            value={statusFilter}
            onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(0); } }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontWeight: 700, px: 1.6, py: 0.6, fontSize: 12,
                '&.Mui-selected': { fontWeight: 800 },
              },
            }}>
            <ToggleButton value="ALL">All
              <Chip size="small" variant="outlined"
                label={counts.ALL}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="PENDING">Pending
              <Chip size="small" color="warning"
                label={counts.PENDING}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="APPROVED">Approved
              <Chip size="small" color="success"
                label={counts.APPROVED}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="REJECTED">Rejected
              <Chip size="small" color="error"
                label={counts.REJECTED}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)}
            sx={{ width: 160 }} />
          <TextField size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)}
            sx={{ width: 160 }} />
          {(from || to) && (
            <Button size="small" onClick={() => { setFrom(''); setTo(''); }}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Clear dates
            </Button>
          )}
        </Box>
      </Card>

      {/* Summary strip — at-a-glance pending stats */}
      {!loading && rows.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' },
          gap: 1.5, mb: 2.5,
        }}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: 'warning.main' }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#ed6c02', 0.14), color: 'warning.main' }}>
                <RequestIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  Pending Requests
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  {rows.length}
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#1565C0', 0.14), color: 'primary.main' }}>
                <BankIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  Total Awaiting
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  GHS {fmtMoney(rows.reduce((s, r) => s + Number(r.amount || 0), 0))}
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: 'success.main' }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#2e7d32', 0.14), color: 'success.main' }}>
                <ApproveIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  Average Request
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  GHS {fmtMoney(rows.reduce((s, r) => s + Number(r.amount || 0), 0) / Math.max(1, rows.length))}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      )}

      {!loading && rows.length === 0 && (
        <Alert
          severity={statusFilter === 'PENDING' ? 'success' : 'info'}
          variant="outlined" sx={{ borderRadius: 2 }}>
          {statusFilter === 'PENDING'
            ? 'The approval queue is empty — no pending reimbursement requests right now.'
            : `No ${statusFilter.toLowerCase()} reimbursements${from || to ? ' in this date range' : ''}.`}
        </Alert>
      )}

      {loading && (
        <Card sx={{ p: 6, textAlign: 'center', mb: 2 }}><CircularProgress /></Card>
      )}

      {!loading && rows.length > 0 && (
        <Card sx={{ borderRadius: 2, overflow: 'hidden',
          border: '1px solid',
          borderColor: (t) =>
            statusFilter === 'PENDING'  ? alpha(t.palette.warning.main, 0.3)
          : statusFilter === 'APPROVED' ? alpha(t.palette.success.main, 0.3)
          : statusFilter === 'REJECTED' ? alpha(t.palette.error.main,   0.3)
          :                               alpha(t.palette.primary.main, 0.3) }}>
          <Box sx={{
            px: 2.5, py: 1.4,
            background: (t) => {
              const c =
                statusFilter === 'PENDING'  ? t.palette.warning.main
              : statusFilter === 'APPROVED' ? t.palette.success.main
              : statusFilter === 'REJECTED' ? t.palette.error.main
              :                               t.palette.primary.main;
              return `linear-gradient(135deg, ${alpha(c, 0.10)} 0%, ${alpha(c, 0.02)} 100%)`;
            },
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Avatar sx={{ width: 30, height: 30,
              bgcolor:
                statusFilter === 'PENDING'  ? 'warning.main'
              : statusFilter === 'APPROVED' ? 'success.main'
              : statusFilter === 'REJECTED' ? 'error.main'
              :                               'primary.main' }}>
              <RequestIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" fontWeight={800}>
                {statusFilter === 'ALL' ? 'All Reimbursements'
                : statusFilter === 'PENDING'  ? 'Pending Reimbursements'
                : statusFilter === 'APPROVED' ? 'Approved Reimbursements'
                :                               'Rejected Reimbursements'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {statusFilter === 'PENDING'
                  ? 'Approve to transfer money into Petty Cash · Reject to send back'
                  : 'Historical record · who decided, when, and why'}
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`${filtered.length}`}
              sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
            <TextField size="small" placeholder="Search ref, source, reason, requester, approver…"
              value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
              sx={{ minWidth: 280, bgcolor: 'background.paper', borderRadius: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: q && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQ('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
              {filtered.slice(page * rpp, (page + 1) * rpp).map(r => {
                const isPending  = r.status === 'PENDING';
                const isApproved = r.status === 'APPROVED';
                const isRejected = r.status === 'REJECTED';
                const accent =
                  isPending  ? '#ed6c02'
                : isApproved ? '#2e7d32'
                : isRejected ? '#d32f2f'
                :              '#1565C0';
                return (
                  <MobileDataCard key={r.id}
                    accent={accent}
                    title={r.reimbursement_no}
                    subtitle={`${fmtDate(r.request_date)} · ${r.source_account}`}
                    status={r.status}
                    statusColor={
                      isPending  ? 'warning'
                    : isApproved ? 'success'
                    : isRejected ? 'error'
                    :              'default'
                    }
                    fields={[
                      { label: 'Reason',     value: r.reason },
                      { label: 'Requested',  value: r.requested_by_name || '—', bold: true },
                      ...((isApproved || isRejected) ? [{
                        label: isApproved ? 'Approved' : 'Rejected',
                        value: (
                          <Box>
                            <Typography fontSize={12} fontWeight={700}>
                              {r.approved_by_name || '—'}
                            </Typography>
                            {r.approved_at && (
                              <Typography fontSize={11} color="text.secondary">
                                {fmtDate(r.approved_at)}
                              </Typography>
                            )}
                            {isRejected && r.rejected_reason && (
                              <Typography fontSize={11} color="text.secondary"
                                sx={{ fontStyle: 'italic', mt: 0.3 }}>
                                {r.rejected_reason}
                              </Typography>
                            )}
                          </Box>
                        ),
                      }] : []),
                    ]}
                    amount={`GHS ${fmtMoney(r.amount)}`}
                    amountColor={isApproved ? 'success.dark' : isRejected ? 'error.main' : 'primary.main'}
                    actions={isPending ? (
                      <>
                        <Button size="small" variant="contained" color="success"
                          startIcon={busy[r.id] ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon fontSize="small" />}
                          onClick={() => approve(r)} disabled={!!busy[r.id]}
                          sx={{ textTransform: 'none', fontWeight: 800, flex: 1 }}>
                          Approve
                        </Button>
                        <Button size="small" variant="outlined" color="error"
                          startIcon={<RejectIcon fontSize="small" />}
                          onClick={() => reject(r)} disabled={!!busy[r.id]}
                          sx={{ textTransform: 'none', fontWeight: 800, flex: 1 }}>
                          Reject
                        </Button>
                      </>
                    ) : isApproved ? (
                      <Button size="small" variant="outlined" color="warning" fullWidth
                        startIcon={busy[r.id] ? <CircularProgress size={14} color="inherit" /> : <ReverseIcon fontSize="small" />}
                        onClick={() => reverse(r)} disabled={!!busy[r.id]}
                        sx={{ textTransform: 'none', fontWeight: 800 }}>
                        Reverse
                      </Button>
                    ) : null}
                  />
                );
              })}
            </Box>
          ) : (
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table stickyHeader size="small" sx={{
              '& .MuiTableCell-head': {
                bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary',
                borderBottom: '2px solid',
                borderColor:
                  statusFilter === 'PENDING'  ? 'warning.light'
                : statusFilter === 'APPROVED' ? 'success.light'
                : statusFilter === 'REJECTED' ? 'error.light'
                :                               'primary.light',
                py: 1.1,
              },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Ref</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Source Account</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Requested by</TableCell>
                  <TableCell>Status / Decision</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.slice(page * rpp, (page + 1) * rpp).map(r => {
                  const isPending  = r.status === 'PENDING';
                  const isApproved = r.status === 'APPROVED';
                  const isRejected = r.status === 'REJECTED';
                  return (
                    <TableRow key={r.id} hover sx={{
                      '& td': { fontSize: 12, py: 1, ...TNUM,
                        borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.04)' },
                      bgcolor:
                        isApproved ? alpha('#2e7d32', 0.03)
                      : isRejected ? alpha('#d32f2f', 0.03)
                      : '#ffffff',
                    }}>
                      <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.reimbursement_no}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{fmtDate(r.request_date)}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <BankIcon fontSize="small" sx={{ color: 'primary.main', fontSize: 14 }} />
                          <span>{r.source_account}</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Tooltip title={r.reason}>
                          <Typography fontSize={12} noWrap>{r.reason}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{r.requested_by_name || '—'}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack direction="column" spacing={0.3}>
                          <Chip size="small" sx={{ width: 'fit-content', height: 19, fontSize: 10, fontWeight: 800 }}
                            color={
                              isPending  ? 'warning'
                            : isApproved ? 'success'
                            : isRejected ? 'error'
                            :              'default'
                            }
                            label={r.status} />
                          {(isApproved || isRejected) && (
                            <Tooltip title={isRejected ? (r.rejected_reason || '') : ''}>
                              <Typography variant="caption" sx={{
                                fontSize: 11, color: 'text.secondary',
                                display: 'block', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                maxWidth: 220,
                              }}>
                                {isApproved ? 'Approved' : 'Rejected'} by{' '}
                                <strong>{r.approved_by_name || '—'}</strong>
                                {r.approved_at && ` · ${fmtDate(r.approved_at)}`}
                                {isRejected && r.rejected_reason && (
                                  <> · <span style={{ fontStyle: 'italic' }}>{r.rejected_reason}</span></>
                                )}
                              </Typography>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800,
                        color: isApproved ? 'success.dark' : isRejected ? 'error.main' : 'primary.main' }}>
                        GHS {fmtMoney(r.amount)}
                      </TableCell>
                      <TableCell align="right">
                        {isPending ? (
                          <Stack direction="row" spacing={0.7} justifyContent="flex-end">
                            <Button size="small" variant="contained" color="success"
                              startIcon={busy[r.id] ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon fontSize="small" />}
                              onClick={() => approve(r)} disabled={!!busy[r.id]}
                              sx={{ textTransform: 'none', fontWeight: 800 }}>
                              Approve
                            </Button>
                            <Button size="small" variant="outlined" color="error"
                              startIcon={<RejectIcon fontSize="small" />}
                              onClick={() => reject(r)} disabled={!!busy[r.id]}
                              sx={{ textTransform: 'none', fontWeight: 800 }}>
                              Reject
                            </Button>
                          </Stack>
                        ) : isApproved ? (
                          <Stack direction="row" spacing={0.7} justifyContent="flex-end">
                            <Tooltip title="Pull this reimbursement back. Only works if the money is still sitting in Petty Cash.">
                              <Button size="small" variant="outlined" color="warning"
                                startIcon={busy[r.id] ? <CircularProgress size={14} color="inherit" /> : <ReverseIcon fontSize="small" />}
                                onClick={() => reverse(r)} disabled={!!busy[r.id]}
                                sx={{ textTransform: 'none', fontWeight: 800 }}>
                                Reverse
                              </Button>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                            No actions
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          <TablePagination component="div"
            count={filtered.length} page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rpp}
            onRowsPerPageChange={(e) => { setRpp(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }} />
        </Card>
      )}
    </Box>
  );
}
